import { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router";
import { LogOut, Sun, Moon, Monitor, Settings, Compass, ArrowRightLeft, Download } from "lucide-react";
import { useSession, signOut } from "@/lib/auth-client";
import { useTheme } from "./ThemeProvider";
import { TOUR_ENABLED } from "@/components/Tour";
import { SIGN_OUT_FAILED_MESSAGE, signOutFinished } from "@/lib/sign-out";
import { clearRbacCache, usePinSession } from "@/hooks/use-rbac";
import { switchPerson } from "@/lib/station-client";
import { readStandalone } from "@/lib/install-platform";
import { logInstallEvent, promptInstall, showInstallMenuItem, useInstallAllowedHere, useInstallPrompt } from "@/lib/install-prompt";

function getInitials(name: string | undefined | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const THEME_OPTIONS = [
  { key: "light" as const, icon: Sun, label: "Light" },
  { key: "dark" as const, icon: Moon, label: "Dark" },
  { key: "system" as const, icon: Monitor, label: "System" },
];

export function UserMenu() {
  const { data: session } = useSession();
  const { theme, setTheme } = useTheme();
  // #4447: in a PIN session the way out is Switch person (ends the PIN
  // session, back to the keypad), not a sign-out that lands on /login.
  const pinSession = usePinSession();
  const [open, setOpen] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // #4974: the way back to an install after Not now on the bar.
  const installPrompt = useInstallPrompt();
  const installAllowed = useInstallAllowedHere();
  const [standalone] = useState(readStandalone);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (!session?.user) return null;

  const { name, email, image } = session.user;

  const handleSignOut = async () => {
    setSignOutFailed(false);
    // #4386: only leave once the server confirms; a timed-out sign-out keeps
    // the cookie, so say so instead of showing a signed-out page.
    const finished = await signOutFinished(() => signOut());
    // #4952 round 3: whatever the server answered, the cache goes, so the
    // next person on this browser starts cold; a sign-out that did not finish
    // costs one cold load.
    clearRbacCache();
    if (finished) {
      window.location.href = "/login";
    } else {
      setSignOutFailed(true);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full p-0.5 hover:ring-2 hover:ring-ring transition-all"
        title={name || email}
      >
        {image ? (
          <img
            src={image}
            alt={name || "User"}
            className="h-8 w-8 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
            {getInitials(name)}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-border bg-popover p-1 shadow-lg z-50">
          <div className="px-3 py-2 border-b border-border mb-1">
            <p className="text-sm font-medium text-popover-foreground truncate">
              {name || "User"}
            </p>
            <p className="text-xs text-muted-foreground truncate">{email}</p>
          </div>

          {/* Theme toggle */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border mb-1">
            <span className="text-xs text-muted-foreground">Theme</span>
            <div className="flex gap-0.5 rounded-md bg-muted p-0.5">
              {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  onClick={() => setTheme(key)}
                  className={`rounded p-1 transition-colors ${
                    theme === key
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  title={label}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          </div>

          <NavLink
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <Settings className="h-4 w-4" />
            Settings
          </NavLink>

          {/* #4974: the browser's own install dialog when it offered one, else
              the /install steps. Never inside the installed app, on a kiosk,
              in a PIN session or on an unattended screen. */}
          {showInstallMenuItem({ standalone, allowed: installAllowed, prompt: installPrompt }) &&
            (installPrompt.event ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void promptInstall("menu");
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Download className="h-4 w-4" />
                Install the app
              </button>
            ) : (
              <NavLink
                to="/install"
                onClick={() => {
                  setOpen(false);
                  logInstallEvent("install_accepted", { from: "menu", via: "steps" });
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Download className="h-4 w-4" />
                Install the app
              </NavLink>
            ))}

          {/* #445: "Take the tour" hidden while the tour is pulled (Hugo 2026-06-17,
              RBAC/security). Re-appears automatically when TOUR_ENABLED flips back. */}
          {TOUR_ENABLED && (
            <button
              onClick={() => {
                setOpen(false);
                // Restart the Walkabout tour from anywhere (useTour in AppShell listens).
                window.dispatchEvent(new Event("crosbe:start-tour"));
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Compass className="h-4 w-4" />
              Take the tour
            </button>
          )}

          {pinSession ? (
            <button
              onClick={() => void switchPerson()}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Switch person
            </button>
          ) : (
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          )}
          {signOutFailed && (
            <p role="alert" className="px-3 pb-2 pt-1 text-xs text-amber-600 dark:text-amber-400">
              {SIGN_OUT_FAILED_MESSAGE}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
